import headhrCourseService from 'ApiService/HeadHR/headhrCourseService';
import headhrStudentService from 'ApiService/HeadHR/headhrStudentService';
import jobServices from 'ApiService/Placement/jobServices';
import branchService from 'ApiService/SuperAdmin/branchService';

import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ThreeDots } from 'react-loader-spinner';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';

import Nodatafound from 'pages/Common/Nodatafound';

import SelectClass from './components/SelectClass';
import StudentOverviewTable from './components/StudentOverviewTable';

const Index = () => {
  const [classData, setClassData] = useState({
    courseId: '',
    batchStructureId: '',
    batchId: '',
  });

  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingStudentData, setLoadingStudentData] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(true);

  const [courses, setCourses] = useState([]);
  const [batchStructures, setBatchStructures] = useState([]);
  const [branches, setBranches] = useState([]);

  const [selectedStandard, setSelectedStandard] = useState(null);
  const [mcqData, setMcqData] = useState(null);
  const [qaData, setQaData] = useState(null);
  const [mcqAssignmentData, setAssignmentMcqData] = useState(null);
  const [qaAssignmentData, setQaAssignmentData] = useState(null);
  const [placementOverviewData, setPlacementOverviewData] = useState(null);
  const [mockData, setMockData] = useState(null);
  const [finalMockData, setFinalMockData] = useState(null);

  /* ================= UNIVERSAL SEARCH ================= */
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [highlightStudentId, setHighlightStudentId] = useState(null);
  const [presetBatches, setPresetBatches] = useState([]);

  const { state, loading, dispatch } = useContext(AuthContext);

  const user = state?.user;
  const selectedBranch = state?.selectedBranch;

  const hrRole = user?.hrRole;
  const allPermission = user?.allPermission;
  const userBranches = user?.branches;

  const hasAccessToAllBranches = hrRole === 'headhr' || allPermission === true;

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoadingBranches(true);

        if (hasAccessToAllBranches) {
          const response = await branchService.allBranches();
          if (response.data.success) {
            setBranches(response.data.allBranches || []);
          }
        } else {
          setBranches(userBranches || []);
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      } finally {
        setLoadingBranches(false);
      }
    };
    if (loading) return;
    if (!user) return;
    fetchBranches();
  }, [loading, user, hasAccessToAllBranches, userBranches]);

  useEffect(() => {
    if (
      !hasAccessToAllBranches &&
      userBranches &&
      userBranches.length === 1 &&
      !selectedBranch
    ) {
      dispatch({ type: 'SET_SELECTED_BRANCH', payload: userBranches[0]._id });
    }
  }, [branches, hasAccessToAllBranches, userBranches, selectedBranch, dispatch]);

  const handleBranchChange = (event) => {
    const selectedBranchId = event.target.value;
    dispatch({ type: 'SET_SELECTED_BRANCH', payload: selectedBranchId });
    resetPage();
  };

  const resetPage = () => {
    setClassData({
      courseId: '',
      batchStructureId: '',
      batchId: '',
    });
    setBatchStructures([]);
    setSelectedStandard(null);
    clearSearch();
  };

  useEffect(() => {
    if (!selectedBranch) return;
    resetPage();
  }, [selectedBranch]);

  useEffect(() => {
    const fetchCourses = async () => {
      setLoadingCourses(true);
      try {
        const res = await headhrCourseService.getAllCourses(selectedBranch);
        if (res.data.success) {
          setCourses(res.data.courses || []);
        }
      } catch {
        toast.error('Failed to fetch courses');
      } finally {
        setLoadingCourses(false);
      }
    };
    if (loading) return;
    if (!user) return;
    if (!selectedBranch) return;

    fetchCourses();
  }, [loading, user, selectedBranch]);

  const fetchBatchStructures = async (courseId) => {
    try {
      const res = await headhrCourseService.getBatchStructuresByCourse(
        courseId
      );
      setBatchStructures(res?.data?.batchStructures || []);
    } catch {
      toast.error('Failed to fetch batch structures');
      setBatchStructures([]);
    }
  };

  const fetchBatchesByStructure = async (structureId) => {
    try {
      const res = await headhrCourseService.getBatchesByBatchStructure(
        structureId,
        selectedBranch
      );
      return res?.data?.batches || [];
    } catch {
      toast.error('Failed to fetch batches');
      return [];
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setLoadingStudentData(true);
      try {
        const practicalResponse = await headhrStudentService.getStudentOverview(
          classData.batchId
        );

        if (practicalResponse.data.success) {
          const sortedStudents =
            practicalResponse.data.Data.studentOverviews.sort((a, b) =>
              a.name.localeCompare(b.name)
            );

          practicalResponse.data.Data.studentOverviews = sortedStudents;
          setSelectedStandard(practicalResponse.data.Data);
        }

        const mcqResponse = await headhrStudentService.getMcqTestOverview(
          classData.batchId
        );
        if (mcqResponse.data.success) setMcqData(mcqResponse.data.data);

        const qaResponse = await headhrStudentService.getQaTestOverview(
          classData.batchId
        );
        if (qaResponse.data.success) setQaData(qaResponse.data.data);

        const mcqAssignmentResponse =
          await headhrStudentService.getMCQAssignmentOverview(classData.batchId);
        if (mcqAssignmentResponse.data.success)
          setAssignmentMcqData(mcqAssignmentResponse.data.data);

        const qaAssignmentResponse =
          await headhrStudentService.getQAAssignmentOverview(classData.batchId);
        if (qaAssignmentResponse.data.success)
          setQaAssignmentData(qaAssignmentResponse.data.data);

        const placementResponse = await jobServices.getStudentWiseOverview(
          classData.courseId,
          classData.batchId
        );
        if (placementResponse.data.success)
          setPlacementOverviewData(placementResponse.data.data);

        const mockResponse = await headhrStudentService.getExternalPracticeOverview(
          classData.batchId
        );
        if (mockResponse.data.success) setMockData(mockResponse.data.data);

        const finalMockResponse = await headhrStudentService.getExternalMockOverview(
          classData.batchId
        );
        if (finalMockResponse.data.success)
          setFinalMockData(finalMockResponse.data.data);
      } catch {
        toast.error('Failed to fetch data');
      } finally {
        setLoadingStudentData(false);
      }
    };
    if (classData.courseId && classData.batchStructureId && classData.batchId) {
      fetchAllData();
    }
  }, [classData]);

  /* ================= UNIVERSAL SEARCH HANDLERS ================= */
  const handleSearchStudents = async () => {
    const q = searchQuery.trim();
    if (!q || q.length < 2) {
      toast.error('Enter at least 2 characters to search');
      return;
    }
    setSearchLoading(true);
    setShowSearchResults(true);
    try {
      const res = await headhrStudentService.searchStudents(q);
      setSearchResults(res.data?.data || []);
    } catch {
      toast.error('Search failed');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearchStudents();
  };

  const handleStudentClick = async (student) => {
    const enrollment = student.enrollments?.[0];
    if (
      !enrollment?.course?._id ||
      !enrollment?.batch?._id ||
      !enrollment?.batchStructure
    ) {
      toast.error('No active enrollment found for this student');
      return;
    }

    setClassData({
      courseId: enrollment.course._id,
      batchStructureId: enrollment.batchStructure,
      batchId: enrollment.batch._id,
    });
    setHighlightStudentId(student._id);

    fetchBatchStructures(enrollment.course._id);
    const fetchedBatches = await fetchBatchesByStructure(
      enrollment.batchStructure
    );
    setPresetBatches(fetchedBatches || []);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setHighlightStudentId(null);
    setPresetBatches([]);
  };

  if (loading || loadingBranches) {
    return (
      <div className="flex h-60 items-center justify-center">Loading...</div>
    );
  }

  return (
    <div className="mt-10 h-full w-full">
      <div className="mb-4 flex items-center justify-center">
        <div className="flex items-center gap-4">
          <label className="text-lg font-semibold text-navy-700 dark:text-white">
            Select Branch:
          </label>
          <select
            className="rounded-lg border border-gray-300 p-2 dark:border-gray-700 dark:bg-navy-800 dark:text-white"
            onChange={handleBranchChange}
            value={selectedBranch || ''}
          >
            <option value="" disabled>
              Select a Branch
            </option>
            {branches.map((branch) => (
              <option key={branch._id} value={branch._id}>
                {branch.name} ({branch.location})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ================= UNIVERSAL SEARCH — always on top ================= */}
      <div className="mx-auto w-[95%] md:px-3 3xl:w-7/12">
        <div className="mb-4 rounded-lg border border-brand-200 bg-brand-50 p-4 dark:border-navy-600 dark:bg-navy-700">
          <h4 className="mb-3 text-center text-lg font-bold">
            Universal Student Search
          </h4>
          <p className="mb-3 text-center text-sm text-gray-500">
            Search across all students by Name, Email, or Mobile Number
          </p>
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search by Name, Email, or Mobile Number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="block w-full rounded-xl border border-gray-300 bg-white p-3 text-sm outline-none focus:border-brand-500 dark:border-navy-500 dark:bg-navy-800 dark:text-white"
            />
            <button
              onClick={handleSearchStudents}
              disabled={searchLoading}
              className="linear cursor-pointer rounded-xl bg-brand-900 px-6 py-3 font-medium text-white transition duration-200 hover:bg-brand-800 disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300"
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
            {showSearchResults && (
              <button
                onClick={clearSearch}
                className="linear cursor-pointer rounded-xl bg-gray-200 px-4 py-3 font-medium text-gray-700 transition duration-200 hover:bg-gray-300 dark:bg-navy-600 dark:text-white dark:hover:bg-navy-500"
              >
                Clear
              </button>
            )}
          </div>

          {showSearchResults && (
            <div className="mt-4">
              {searchLoading ? (
                <div className="flex justify-center py-8">
                  <ThreeDots color="#5431ed" />
                </div>
              ) : searchResults.length === 0 ? (
                <Nodatafound title="No students found matching your search" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white">
                          Sr No
                        </th>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white">
                          Name
                        </th>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white">
                          Email
                        </th>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white">
                          Contact Number
                        </th>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white">
                          Gender
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((student, index) => (
                        <tr
                          key={student._id}
                          onClick={() => handleStudentClick(student)}
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-600"
                        >
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-gray-700 dark:text-white">
                            {index + 1}
                          </td>
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-gray-700 dark:text-white">
                            {student.name}
                          </td>
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm text-gray-700 dark:text-white">
                            {student.email}
                          </td>
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm text-gray-700 dark:text-white">
                            {student.contactNumber}
                          </td>
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm text-gray-700 dark:text-white">
                            {student.gender}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-2 text-center text-xs text-gray-400">
                    Showing up to 50 results. Click a row to view details.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!selectedBranch ? (
        <div className="mx-auto flex h-40 w-[95%] items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 md:px-3 3xl:w-7/12">
          <p className="text-sm text-gray-500">
            Select a branch to view class data
          </p>
        </div>
      ) : (
        <>
          <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600" />

          <div className="mx-auto h-full w-[95%] md:px-3 lg:mt-5 3xl:w-7/12">
            <Card extra="-mt-[280px] w-full md:-mt-[340px] md:px-[70px] p-3">
              <UseContextProvider>
                <SelectClass
                  classData={classData}
                  setClassData={setClassData}
                  courses={courses}
                  batchStructures={batchStructures}
                  fetchBatchStructures={fetchBatchStructures}
                  fetchBatchesByStructure={fetchBatchesByStructure}
                  loadingCourses={loadingCourses}
                  presetBatches={presetBatches}
                />

                {selectedStandard && (
                  <StudentOverviewTable
                    data={selectedStandard}
                    mcqData={mcqData}
                    qaData={qaData}
                    mcqAssignmentData={mcqAssignmentData}
                    qaAssignmentData={qaAssignmentData}
                    placementOverviewData={placementOverviewData}
                    mockData={mockData}
                    finalMockData={finalMockData}
                    isLoading={loadingStudentData}
                    highlightStudentId={highlightStudentId}
                  />
                )}
              </UseContextProvider>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default Index;
